"use client";

import parsePhoneNumberFromString, { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

import { marketingDataSchema } from "./marketingDataSchema";

import { convertDistance } from "@/lib/utils";

// Type for validation message translator (e.g. from useTranslations("BankValuation.form.validation"))
export type BankValuationValidationTranslator = (key: string) => string;

const DEFAULT_MESSAGES = {
  nameRequired: "Name is required",
  clientNameRequired: "Client name is required",
  emailRequired: "Email is required",
  phoneRequired: "Phone number is required",
  phoneInvalid: "Please enter a valid phone number",
  makeRequired: "Make is required",
  makeNameRequired: "Make name is required",
  modelRequired: "Model is required",
  modelNameRequired: "Model name is required",
  specificationRequired: "Specification is required",
  specsRequired: "Specification name is required",
  engineTypeRequired: "Engine type is required",
  yearRequired: "Year is required",
  colorRequired: "Color is required",
  vinRequired: "VIN is required",
  vinLength: "VIN must be exactly 17 characters",
  vinInvalid: "VIN contains invalid characters (I, O, Q and i, o, q are not allowed)",
  engineNumberInvalid: "Engine number must contain only letters and numbers",
  mileagePositive: "Mileage must be a positive number",
  engineSizeRange: "Engine size must be between 0.5 and 10",
  bankerEmailRequired: "Banker's email is required",
  bankerEmailInvalid: "Please enter a valid email address",
  bankNameRequired: "Bank name is required",
  valueGreaterThanZero: "Expected price must be greater than 0",
  required: "Required",
};

const getMessages = (t?: BankValuationValidationTranslator): typeof DEFAULT_MESSAGES =>
  t
    ? (Object.fromEntries(
        (Object.keys(DEFAULT_MESSAGES) as (keyof typeof DEFAULT_MESSAGES)[]).map((k) => [k, t(k)]),
      ) as typeof DEFAULT_MESSAGES)
    : DEFAULT_MESSAGES;

/** Creates BankValuationFirstStepSchema with optional translated validation messages */
export const createBankValuationFirstStepSchema = (t?: BankValuationValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    name: z.string({ required_error: m.nameRequired }).trim().nonempty({ message: m.nameRequired }),
    clientName: z.string({ required_error: m.clientNameRequired }).trim().nonempty({ message: m.clientNameRequired }),
    email: z
      .string({ required_error: m.emailRequired })
      .trim()
      .email(m.emailRequired)
      .nonempty({ message: m.emailRequired }),
    phone: z
      .string({ required_error: m.phoneRequired })
      .min(8, m.phoneRequired)
      .refine((value) => {
        if (!value) return false;
        const phoneNumber = parsePhoneNumberFromString(value);
        if (!phoneNumber) return false;
        return isValidPhoneNumber(value);
      }, m.phoneInvalid),
    sessionId: z.string().trim().optional(),
    marketing: marketingDataSchema,
  });
};

/** Creates BankValuationSecondStepSchema with optional translated validation messages */
export const createBankValuationSecondStepSchema = (t?: BankValuationValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    make: z.string({ required_error: m.makeRequired }).trim().nonempty({ message: m.makeRequired }),
    makeName: z.string({ required_error: m.makeNameRequired }).nonempty({ message: m.makeNameRequired }),
    model: z
      .union([z.string().trim(), z.null(), z.undefined()])
      .transform((val) => val ?? "")
      .pipe(z.string().nonempty({ message: m.modelRequired })),
    modelName: z.string({ required_error: m.modelNameRequired }).nonempty({ message: m.modelNameRequired }),
    trim: z
      .union([z.string().trim(), z.null()])
      .transform((val) => val || "")
      .optional(),
    trimName: z
      .union([z.string(), z.null()])
      .transform((val) => val || "")
      .optional(),
    trimOption: z
      .union([z.string().trim(), z.null()])
      .transform((val) => val || "")
      .optional(),
    trimOptionName: z
      .union([z.string(), z.null()])
      .transform((val) => val || "")
      .optional(),
    specification: z
      .string({ required_error: m.specificationRequired })
      .trim()
      .nonempty({ message: m.specificationRequired }),
    specs: z.string({ required_error: m.specsRequired }).nonempty({ message: m.specsRequired }),
    engineType: z.string({ required_error: m.engineTypeRequired }).nonempty({ message: m.engineTypeRequired }),
    year: z.string({ required_error: m.yearRequired }).trim().nonempty({ message: m.yearRequired }),
    color: z.string({ required_error: m.colorRequired }).trim().nonempty({ message: m.colorRequired }),
    vin: z
      .string({ required_error: m.vinRequired })
      .trim()
      .nonempty({ message: m.vinRequired })
      .length(17, { message: m.vinLength })
      .regex(/^[A-HJ-NPR-Za-hj-npr-z0-9]+$/, { message: m.vinInvalid }),
    engineNumber: z.preprocess(
      (val) => (val == null ? "" : String(val).trim()),
      z.string().regex(/^[a-zA-Z0-9]*$/, { message: m.engineNumberInvalid }),
    ),
    mileage: z
      .union([
        z.string({ required_error: m.required }).transform((val) => (val === "" ? "" : Number(val))),
        z.number(),
        z.literal(""),
      ])
      .refine((val) => val === "" || val >= 0, { message: m.mileagePositive }),
    clientMileage: z
      .union([
        z.string({ required_error: m.required }).transform((val) => (val === "" ? "" : Number(val))),
        z.number(),
        z.literal(""),
      ])
      .refine((val) => val === "" || val >= 0, { message: m.mileagePositive })
      .optional(),
    mileageUnit: z.enum(["km", "miles"]).default("km"),
    engineSize: z
      .union([z.string().transform((val) => (val === "" ? "" : Number(val))), z.number(), z.literal(""), z.null()])
      .refine((val) => val === "" || val === null || (val >= 0.5 && val <= 10), { message: m.engineSizeRange })
      .optional(),
    description: z.string().trim().optional(),
  });
};

export const ThirdStepSchema = z.object({
  images: z.array(z.any()),
});

export type ThirdStepSchemaType = z.infer<typeof ThirdStepSchema>;

/** Creates BankDetailsSchema with optional translated validation messages */
export const createBankDetailsSchema = (t?: BankValuationValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    bankerEmail: z
      .string({ required_error: m.bankerEmailRequired })
      .trim()
      .email(m.bankerEmailInvalid)
      .nonempty({ message: m.bankerEmailRequired }),
    bankName: z.string({ required_error: m.bankNameRequired }).trim().nonempty({ message: m.bankNameRequired }),
    relationshipManagerName: z.string().trim().optional(),
    bankApplicationId: z.string().trim().optional(),
  });
};

/** Creates BankValuationEstimatedPriceStepSchema with optional translated validation messages */
export const createBankValuationEstimatedPriceStepSchema = (t?: BankValuationValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    value: z
      .union([z.string().transform((val) => (val === "" ? 0 : Number(val))), z.number()])
      .refine((val) => val > 0, { message: m.valueGreaterThanZero }),
    currency: z.string().trim().default("AED"),
    dealDriveEstimatedPrice: z.number().optional().default(0),
  });
};

export const BankValuationStripePaymentStepSchema = z.object({
  poBox: z.string().trim().optional(),
  emirate: z
    .enum(["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Umm Al Quwain", "Fujairah"])
    .default("Dubai"),
  trn: z.string().trim().optional(),
  businessName: z.string().trim().optional(),
});

/** Default schemas (English) for backward compatibility */
export const BankValuationFirstStepSchema = createBankValuationFirstStepSchema();
export const BankValuationSecondStepSchema = createBankValuationSecondStepSchema();
export const BankDetailsSchema = createBankDetailsSchema();
export const BankValuationEstimatedPriceStepSchema = createBankValuationEstimatedPriceStepSchema();

/** Creates BankValuationFormSchema with optional translated validation messages */
export const createBankValuationFormSchema = (t?: BankValuationValidationTranslator) =>
  createBankValuationFirstStepSchema(t)
    .merge(createBankValuationSecondStepSchema(t))
    .merge(ThirdStepSchema)
    .transform((data: any) => {
      const clientMileageValue = data.mileage === "" ? 0 : Number(data.mileage);
      const mileageUnit = data.mileageUnit || "km";
      const mileageInKm =
        mileageUnit === "miles" ? convertDistance(clientMileageValue, { from: "miles", to: "km" }) : clientMileageValue;

      return {
        name: data.name,
        clientName: data.clientName,
        phone: data.phone,
        make: data.make,
        makeName: data.makeName,
        model: data.model,
        modelName: data.modelName,
        trim: data.trim,
        trimName: data.trimName,
        specification: data.specification,
        specs: data.specs,
        year: data.year,
        color: data.color,
        clientMileage: clientMileageValue,
        mileage: mileageInKm,
        mileageUnit: mileageUnit,
        engineSize: data.engineSize === "" ? 0 : Number(data.engineSize),
        engineNumber: data.engineNumber ?? "",
        vin: data.vin,
        images: (data.images?.filter((img: { url?: string }) => img?.url)?.map((img: { url: string }) => img.url) ??
          []) as string[],
        marketing: data.marketing,
      };
    });

/** Creates BankValuationCompleteFormSchema with optional translated validation messages */
export const createBankValuationCompleteFormSchema = (t?: BankValuationValidationTranslator) =>
  createBankValuationFirstStepSchema(t)
    .merge(createBankValuationSecondStepSchema(t))
    .merge(ThirdStepSchema)
    .merge(createBankDetailsSchema(t))
    .merge(createBankValuationEstimatedPriceStepSchema(t))
    .merge(BankValuationStripePaymentStepSchema);

export const BankValuationFormSchema = createBankValuationFormSchema();
export const BankValuationCompleteFormSchema = createBankValuationCompleteFormSchema();

export type BankValuationFirstStepSchemaType = z.infer<ReturnType<typeof createBankValuationFirstStepSchema>>;
export type BankValuationSecondStepSchemaType = z.infer<ReturnType<typeof createBankValuationSecondStepSchema>>;
export type BankDetailsSchemaType = z.infer<ReturnType<typeof createBankDetailsSchema>>;
export type BankValuationEstimatedPriceStepSchemaType = z.infer<
  ReturnType<typeof createBankValuationEstimatedPriceStepSchema>
>;
export type BankValuationFormSchemaType = z.infer<ReturnType<typeof createBankValuationFormSchema>>;
export type BankValuationCompleteFormSchemaType = z.infer<ReturnType<typeof createBankValuationCompleteFormSchema>>;
